import { useState } from "react";
import { Loader2, Crown, Check } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { adminSetUserTier } from "../lib/api";

const TIERS = [
  { id: "basic", label: "Basic", price: "$9/mo" },
  { id: "standard", label: "Standard", price: "$16/mo" },
  { id: "pro", label: "Pro", price: "$22/mo" },
  { id: "pro_plus", label: "Pro+", price: "$29/mo" },
];

const STATUSES = [
  { id: "active", label: "Active" },
  { id: "trialing", label: "Trialing" },
  { id: "beta", label: "Pre-launch (beta)" },
  { id: "lapsed", label: "Lapsed / no access" },
];

/**
 * Super-admin only (dffrench14@gmail.com). Lets the founder grant any
 * teacher any tier's features bypassing Stripe — e.g. give a Basic
 * customer Standard features as a goodwill gesture, or unlock Pro
 * features for an early-access partner without charging them.
 *
 * Renders as a click-to-open dropdown in the Tier column. Two-step:
 *   1. Pick a tier → marks the user as `active` on that tier.
 *   2. Or pick a status to revoke (lapsed) or mark trialing/beta.
 *
 * Calls `POST /api/admin/set-user-tier`. Optimistically patches the
 * parent table on success.
 */
const TierOverrideDropdown = ({ email, currentTier, currentStatus, onChange }) => {
  const [busy, setBusy] = useState(false);

  const handlePick = async (tier_id, status) => {
    setBusy(true);
    try {
      const res = await adminSetUserTier(email, tier_id, status);
      onChange?.(res?.user || { subscription_tier: tier_id, subscription_status: status });
      toast.success(`Updated ${email}`, {
        description: `Now ${tier_id}${status !== "active" ? ` (${status})` : ""}.`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update tier.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`tier-override-${email}`}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-[#4a6984]/35 text-[10px] font-bold uppercase tracking-[0.12em] text-[#4a6984] hover:bg-[#f4f5f7] hover:border-[#4a6984]/60 transition disabled:opacity-40"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crown className="h-3 w-3" />}
        Override
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
          Grant tier
        </DropdownMenuLabel>
        {TIERS.map((t) => (
          <DropdownMenuItem
            key={t.id}
            data-testid={`tier-override-pick-${t.id}-${email}`}
            onClick={() => handlePick(t.id, "active")}
            className="flex items-center justify-between gap-3 cursor-pointer"
          >
            <span className="font-semibold text-[#1c2d3d]">{t.label}</span>
            <span className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#4a6984]/60">{t.price}</span>
              {currentTier === t.id && currentStatus === "active" && (
                <Check className="h-3 w-3 text-emerald-600" />
              )}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
          Status
        </DropdownMenuLabel>
        {STATUSES.filter((s) => s.id !== "active").map((s) => (
          <DropdownMenuItem
            key={s.id}
            data-testid={`tier-override-status-${s.id}-${email}`}
            onClick={() => handlePick(currentTier || "basic", s.id)}
            className="flex items-center justify-between gap-3 cursor-pointer"
          >
            <span className="font-medium text-[#4a6984]">{s.label}</span>
            {currentStatus === s.id && <Check className="h-3 w-3 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TierOverrideDropdown;
